import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radio, Sparkles, ChevronRight, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TAPIN_RADIO_LOGO } from "../lib/radioBrand";

/**
 * RadioHostHub — landing page for creators who want to host on Tap IN Radio.
 * Shows stations they're approved for, plus an apply form for others.
 */
export default function RadioHostHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stations, setStations] = useState([]);
  const [applyStation, setApplyStation] = useState("");
  const [bio, setBio] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/radio/host/me").then((r) => setMe(r.data)).catch(() => setMe({ can_host: false }));
    api.get("/radio/stations").then((r) => setStations(r.data || []));
  }, [user]);

  const apply = async (e) => {
    e.preventDefault();
    if (!applyStation) return;
    setSubmitting(true);
    try {
      await api.post("/radio/host/apply", { station_slug: applyStation, bio });
      toast.success("Application sent! Admin will review shortly.");
      setBio("");
      const r = await api.get("/radio/host/me");
      setMe(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  if (!user) return <div className="min-h-screen bg-[#05060a] text-white flex items-center justify-center">Sign in to host radio.</div>;

  const approved = me?.host_stations || [];
  const pending = me?.pending_applications || [];

  return (
    <div data-testid="radio-host-hub" className="min-h-screen bg-[#05060a] text-white pb-[160px]">
      <section
        className="relative"
        style={{
          minHeight: 340,
          backgroundImage: "linear-gradient(135deg, rgba(220,38,38,0.35), rgba(5,6,10,0.95)), url(https://images.unsplash.com/photo-1485579149621-3123dd979885?q=80&w=1400&auto=format&fit=crop)",
          backgroundSize: "cover",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-28 md:pt-32 pb-10 flex items-center gap-6 flex-wrap">
          <img
            src={TAPIN_RADIO_LOGO}
            alt="Tap IN Radio"
            className="w-28 h-28 sm:w-36 sm:h-36 object-contain shrink-0 drop-shadow-[0_4px_24px_rgba(220,38,38,0.35)]"
            data-testid="tapin-radio-masthead"
          />
          <div className="min-w-0 flex-1">
            <div className="text-red-400 uppercase tracking-[0.3em] text-[11px] font-black mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> TAP IN RADIO · Creator Studio
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black mb-2">Run your own Tap IN Radio show</h1>
            <p className="text-white/75 text-base max-w-[640px]">
              DJ-style turntables, console mixer, crossfader, voice FX, mic input and a built-in ad scheduler.
              Your listeners tap the station — your live mix takes over. <span className="italic text-red-300/90">Keeping you tapped in.</span>
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 space-y-8">
        {/* Approved stations */}
        <section>
          <h2 className="font-display text-2xl font-black mb-3">Your Stations</h2>
          {approved.length === 0 ? (
            <div className="rounded-lg bg-white/5 border border-white/10 p-4 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="text-white font-bold">Not a host yet?</div>
                <div className="text-white/60 text-sm">Pitch your show — admins review in ~48h. Approved hosts get a station + studio access.</div>
              </div>
              <a
                href="/radio/apply-host"
                data-testid="radio-apply-host-cta"
                className="bg-tv-accent hover:bg-yellow-400 text-black font-black px-4 py-2 rounded"
              >Apply to be a Radio Host</a>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {approved.map((slug) => {
                const st = stations.find((s) => s.slug === slug);
                return (
                  <div
                    key={slug}
                    data-testid={`radio-host-station-${slug}`}
                    className="rounded-xl bg-white/5 border border-tv-accent/30 p-4 flex items-center gap-3"
                  >
                    <div
                      className="w-14 h-14 rounded bg-white/10 shrink-0"
                      style={{ backgroundImage: `url(${st?.cover_url})`, backgroundSize: "cover", backgroundPosition: "center" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-white">{st?.name || slug}</div>
                      <div className="text-white/50 text-xs">Approved host</div>
                    </div>
                    <button
                      data-testid={`radio-host-studio-${slug}`}
                      onClick={() => navigate(`/radio/host/studio/${slug}`)}
                      className="bg-tv-accent hover:bg-yellow-400 text-black font-bold text-sm px-3 py-2 rounded"
                    >
                      Open Studio <ChevronRight className="w-3.5 h-3.5 inline" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Pending */}
        {pending.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-black mb-3">Pending</h2>
            <div className="space-y-2">
              {pending.map((p) => (
                <div key={p.id} className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-amber-100 text-sm">
                  Awaiting admin approval for <b>{p.station_slug}</b>.
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Apply */}
        <section>
          <h2 className="font-display text-2xl font-black mb-3">Apply to host</h2>
          <form onSubmit={apply} className="bg-white/5 border border-white/10 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="radio-host-apply-form">
            <select
              data-testid="radio-host-apply-station"
              required
              value={applyStation}
              onChange={(e) => setApplyStation(e.target.value)}
              className="bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-tv-accent outline-none"
            >
              <option value="">Pick a station…</option>
              {stations.filter((s) => !approved.includes(s.slug) && !pending.some((p) => p.station_slug === s.slug)).map((s) => (
                <option key={s.id} value={s.slug}>{s.name}</option>
              ))}
            </select>
            <textarea
              data-testid="radio-host-apply-bio"
              rows={3}
              placeholder="Tell us about yourself & your show (optional)…"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="md:row-span-2 bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-tv-accent outline-none"
            />
            <button
              data-testid="radio-host-apply-submit"
              type="submit"
              disabled={submitting || !applyStation}
              className="bg-tv-accent text-black font-bold text-sm px-4 py-2 rounded disabled:opacity-40 flex items-center justify-center gap-1"
            >
              <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Submit application"}
            </button>
          </form>
        </section>

        <section>
          <Link to="/radio" className="text-white/50 hover:text-white text-sm inline-flex items-center gap-1">
            <Radio className="w-4 h-4" /> Back to Tap IN Radio
          </Link>
        </section>
      </div>
    </div>
  );
}
